import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getZonesByWarehouse = createAsyncThunk(
  "Zone/getByWarehouse",
  async (warehouseId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.ZONE.BY_WAREHOUSE(warehouseId));
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const initializeDefaultZones = createAsyncThunk(
  "Zone/initialize",
  async (warehouseId, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.ZONE.INITIALIZE(warehouseId));
      // Refresh zones after initialization
      dispatch(getZonesByWarehouse(warehouseId));
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const createZone = createAsyncThunk(
  "Zone/create",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.ZONE.SLUG, payload);
      // Refresh zones after creation
      dispatch(getZonesByWarehouse(payload.warehouseId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const updateZone = createAsyncThunk(
  "Zone/update",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(API_URL.ZONE.SLUG, payload);
      // Refresh zones after update
      dispatch(getZonesByWarehouse(payload.warehouseId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const deleteZone = createAsyncThunk(
  "Zone/delete",
  async ({ id, warehouseId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(`${API_URL.ZONE.SLUG}/${id}`);
      // Refresh zones after deletion
      dispatch(getZonesByWarehouse(warehouseId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getBinsByZone = createAsyncThunk(
  "Bin/getByZone",
  async ({ zoneId, searchText = "", pageNumber = 1, pageSize = 10 }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        PageNumber: pageNumber,
        PageSize: pageSize,
      });

      if (searchText) {
        params.append("SearchText", searchText);
      }

      const { data } = await axios.get(`${API_URL.BIN.BY_ZONE(zoneId)}?${params.toString()}`);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createBin = createAsyncThunk(
  "Bin/create",
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.post(API_URL.BIN.SLUG, payload);
      // Refresh zones to update bin count
      dispatch(getZonesByWarehouse(payload.warehouseId));
      // Refresh bin list for the current zone with current search text
      const state = getState();
      const { bin_pagination, bin_search_text } = state.zone;
      dispatch(getBinsByZone({
        zoneId: payload.zoneId,
        searchText: bin_search_text,
        pageNumber: bin_pagination.currentPage,
        pageSize: bin_pagination.pageSize,
      }));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const updateBin = createAsyncThunk(
  "Bin/update",
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(API_URL.BIN.SLUG, payload);
      // Refresh zones to update bin count
      dispatch(getZonesByWarehouse(payload.warehouseId));
      // Refresh bin list for the current zone with current search text
      const state = getState();
      const { bin_pagination, bin_search_text } = state.zone;
      dispatch(getBinsByZone({
        zoneId: payload.zoneId,
        searchText: bin_search_text,
        pageNumber: bin_pagination.currentPage,
        pageSize: bin_pagination.pageSize,
      }));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const deleteBin = createAsyncThunk(
  "Bin/delete",
  async ({ id, warehouseId, zoneId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.delete(`${API_URL.BIN.SLUG}/${id}`);
      // Refresh zones to update bin count
      dispatch(getZonesByWarehouse(warehouseId));
      // Refresh bin list for the current zone with current search text
      const state = getState();
      const { bin_pagination, bin_search_text } = state.zone;
      dispatch(getBinsByZone({
        zoneId: zoneId,
        searchText: bin_search_text,
        pageNumber: bin_pagination.currentPage,
        pageSize: bin_pagination.pageSize,
      }));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const bulkImportBinsFile = createAsyncThunk(
  "Bin/bulkImportFile",
  async ({ warehouseId, zoneId, file }, { rejectWithValue, dispatch, getState }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await axios.post(
        `${API_URL.BIN.BULK_IMPORT_FILE}?warehouseId=${warehouseId}&zoneId=${zoneId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      // Refresh zones to update bin count
      dispatch(getZonesByWarehouse(warehouseId));
      // Refresh bin list for the current zone
      const state = getState();
      const { bin_pagination, bin_search_text } = state.zone;
      dispatch(getBinsByZone({
        zoneId: zoneId,
        searchText: bin_search_text,
        pageNumber: bin_pagination.currentPage,
        pageSize: bin_pagination.pageSize,
      }));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

const zoneSlice = createSlice({
  name: "zone",
  initialState: {
    zone_list: [],
    bin_list: [],
    bin_pagination: {
      currentPage: 1,
      pageSize: 10,
      totalCount: 0,
      totalPages: 0,
    },
    bin_search_text: "",
    current_zone: null,
    current_bin: null,
    loading: false,
  },
  reducers: {
    setCurrentZone(state, action) {
      state.current_zone = action.payload;
    },
    setCurrentBin(state, action) {
      state.current_bin = action.payload;
    },
    resetCurrentZone(state) {
      state.current_zone = null;
    },
    resetCurrentBin(state) {
      state.current_bin = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getZonesByWarehouse.pending, (state) => {
        state.loading = true;
      })
      .addCase(getZonesByWarehouse.fulfilled, (state, action) => {
        state.zone_list = action.payload.data;
        state.loading = false;
      })
      .addCase(getZonesByWarehouse.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getBinsByZone.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBinsByZone.fulfilled, (state, action) => {
        state.bin_list = action.payload.data.list || [];
        state.bin_pagination = {
          currentPage: action.payload.data.pageNumber || 1,
          pageSize: action.payload.data.pageSize || 10,
          totalCount: action.payload.data.totalRecords || 0,
          totalPages: action.payload.data.totalPages || 0,
        };
        state.bin_search_text = action.meta.arg.searchText || "";
        state.loading = false;
      })
      .addCase(getBinsByZone.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setCurrentZone, setCurrentBin, resetCurrentZone, resetCurrentBin } =
  zoneSlice.actions;

export default zoneSlice.reducer;
